import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, CreditCard, Search, ExternalLink } from "lucide-react";
import { API, getTours } from "../../lib/adventureApi";
import { authHeader } from "../../lib/auth";

export default function WeTravelAdmin() {
  const [globalUrl, setGlobalUrl] = useState("");
  const [links, setLinks] = useState({});
  const [tours, setTours] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    getTours().then((all) => setTours(all.sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    fetch(`${API}/admin/wetravel`, { headers: { ...authHeader() } })
      .then((r) => r.json())
      .then((d) => { setGlobalUrl(d.global_url || ""); setLinks(d.links || {}); setUpdatedAt(d.updated_at || null); })
      .catch(() => toast.error("Could not load WeTravel links"))
      .finally(() => setLoading(false));
  }, []);

  const setLink = (slug, v) => setLinks((p) => ({ ...p, [slug]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/wetravel`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ global_url: globalUrl, links }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "");
      const d = await res.json();
      setLinks(d.links || {});
      setUpdatedAt(d.updated_at || null);
      toast.success("WeTravel links saved", { description: "Reserve buttons now open these pages." });
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center text-ink-secondary"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading…</div>;

  const filtered = tours.filter((t) => t.name.toLowerCase().includes(q.toLowerCase()));
  const setCount = Object.values(links).filter((v) => (v || "").trim()).length;

  return (
    <div data-testid="wetravel-admin" className="max-w-3xl">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 bg-moss rounded-sm flex items-center justify-center"><CreditCard className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="font-display font-extrabold text-xl tracking-tight leading-none">WeTravel Payment Links</h2>
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-1">Global / UK / EU deposits · Australia uses Stripe</p>
        </div>
      </div>
      <p className="text-sm text-ink-secondary mt-3 mb-6">
        Paste each tour's WeTravel trip page URL. When a traveller (outside Australia) taps <b>Pay deposit now</b>, we open that tour's WeTravel page.
        Tours without their own link fall back to the global link below.
      </p>

      <div className="border border-sand-dark rounded-md bg-white p-5 mb-5">
        <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Global fallback WeTravel link</label>
        <input data-testid="wetravel-global" value={globalUrl} onChange={(e) => setGlobalUrl(e.target.value)}
          placeholder="https://www.wetravel.com/trips/your-default-deposit"
          className="mt-1.5 w-full h-11 px-3 rounded-sm border border-sand-dark text-ink text-sm font-mono focus:border-moss outline-none" />
      </div>

      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="wetravel-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tours…"
            className="w-full h-10 pl-9 pr-3 rounded-sm border border-sand-dark text-sm focus:border-moss outline-none" />
        </div>
        <span className="font-mono text-[11px] text-ink-muted">{setCount}/{tours.length} tours linked</span>
      </div>

      <div className="border border-sand-dark rounded-md bg-white divide-y divide-sand-dark max-h-[48vh] overflow-y-auto thin-scroll">
        {filtered.map((t) => (
          <div key={t.slug} className="flex items-center gap-3 p-3" data-testid={`wetravel-row-${t.slug}`}>
            <div className="min-w-0 w-56 shrink-0">
              <p className="font-display font-semibold text-sm text-ink leading-tight truncate">{t.name}</p>
              <p className="font-mono text-[10px] uppercase tracking-wide text-ink-muted">{t.destination}</p>
            </div>
            <input data-testid={`wetravel-link-${t.slug}`} value={links[t.slug] || ""} onChange={(e) => setLink(t.slug, e.target.value)}
              placeholder="https://www.wetravel.com/trips/…"
              className="flex-1 h-10 px-3 rounded-sm border border-sand-dark text-ink text-sm font-mono focus:border-moss outline-none" />
            {(links[t.slug] || "").trim() && (
              <a href={links[t.slug]} target="_blank" rel="noreferrer" className="text-ink-muted hover:text-terracotta shrink-0" title="Open link">
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        ))}
        {filtered.length === 0 && <p className="text-sm text-ink-muted p-4 text-center">No tours match.</p>}
      </div>

      <div className="flex items-center gap-4 mt-5">
        <button data-testid="wetravel-save" onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-5 h-11 rounded-sm font-semibold transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save links
        </button>
        {updatedAt && <span className="font-mono text-[11px] text-ink-muted">Last updated {new Date(updatedAt).toLocaleString()}</span>}
      </div>
    </div>
  );
}
